import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  CalendarIcon,
  ClockIcon,
  TagIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  publishDate: string;
  readTime: string;
  image: string;
  featured: boolean;
  published?: boolean;
  author?: string;
  createdAt?: string;
  updatedAt?: string;
}

const Blog: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const categories = [
    { id: 'all', name: t('blog.categories.all') },
    { id: 'ai', name: t('blog.categories.ai') },
    { id: 'automation', name: t('blog.categories.automation') },
    { id: 'data-science', name: t('blog.categories.dataScience') },
    { id: 'business', name: t('blog.categories.business') },
    { id: 'technology', name: t('blog.categories.technology') }
  ];

  // Load blog posts from API
  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/blogs');
        const result = await response.json();
        
        if (result.success && result.data) {
          setBlogPosts(result.data);
        } else {
          // Fallback to hardcoded posts if API fails
          setBlogPosts(fallbackBlogPosts);
        }
      } catch (err) {
        console.error('Error loading blog posts:', err);
        // Use fallback data
        setBlogPosts(fallbackBlogPosts);
      } finally {
        setLoading(false);
      }
    };

    loadBlogPosts();
  }, [t]);

  // Fallback blog posts (keep the existing hardcoded data as backup)
  const fallbackBlogPosts: BlogPost[] = [
    {
      id: '1',
      title: t('blog.post1.title'),
      excerpt: t('blog.post1.excerpt'),
      content: t('blog.post1.content'),
      category: 'ai',
      tags: ['AI', 'Machine Learning', 'Business Strategy'],
      publishDate: t('blog.post1.date'),
      readTime: t('blog.post1.readTime'),
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=AI%20transformation%20in%20business%20with%20digital%20networks%20and%20data%20visualization%2C%20modern%20corporate%20setting%2C%20blue%20and%20purple%20tones&image_size=landscape_4_3',
      featured: true
    },
    {
      id: '2',
      title: t('blog.post2.title'),
      excerpt: t('blog.post2.excerpt'),
      content: t('blog.post2.content'),
      category: 'automation',
      tags: ['Automation', 'Workflow', 'Efficiency'],
      publishDate: t('blog.post2.date'),
      readTime: t('blog.post2.readTime'),
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Business%20process%20automation%20with%20robotic%20arms%20and%20digital%20workflows%2C%20futuristic%20office%20environment%2C%20clean%20design&image_size=landscape_4_3',
      featured: true
    },
    {
      id: '3',
      title: t('blog.post3.title'),
      excerpt: t('blog.post3.excerpt'),
      content: t('blog.post3.content'),
      category: 'data-science',
      tags: ['Data Science', 'Analytics', 'Insights'],
      publishDate: t('blog.post3.date'),
      readTime: t('blog.post3.readTime'),
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Data%20analytics%20dashboard%20with%20charts%20and%20graphs%2C%20data%20visualization%20interface%2C%20professional%20design%2C%20green%20accents&image_size=landscape_4_3',
      featured: false
    },
    {
      id: '4',
      title: t('blog.post4.title'),
      excerpt: t('blog.post4.excerpt'),
      content: t('blog.post4.content'),
      category: 'business',
      tags: ['Cross-Cultural', 'Business', 'Communication'],
      publishDate: t('blog.post4.date'),
      readTime: t('blog.post4.readTime'),
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=International%20business%20meeting%20with%20diverse%20team%20members%2C%20global%20collaboration%2C%20modern%20conference%20room&image_size=landscape_4_3',
      featured: false
    },
    {
      id: '5',
      title: t('blog.post5.title'),
      excerpt: t('blog.post5.excerpt'),
      content: t('blog.post5.content'),
      category: 'technology',
      tags: ['No-Code', 'Technology', 'Innovation'],
      publishDate: t('blog.post5.date'),
      readTime: t('blog.post5.readTime'),
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=No-code%20platform%20interface%20with%20drag%20and%20drop%20elements%2C%20visual%20programming%2C%20modern%20UI%20design&image_size=landscape_4_3',
      featured: false
    },
    {
      id: '6',
      title: t('blog.post6.title'),
      excerpt: t('blog.post6.excerpt'),
      content: t('blog.post6.content'),
      category: 'ai',
      tags: ['AI Ethics', 'Responsible AI', 'Future'],
      publishDate: t('blog.post6.date'),
      readTime: t('blog.post6.readTime'),
      image: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=AI%20ethics%20concept%20with%20balance%20scales%20and%20digital%20brain%2C%20philosophical%20technology%20theme%2C%20thoughtful%20design&image_size=landscape_4_3',
      featured: false
    }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = searchTerm === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  const openPostModal = (post: BlogPost) => {
    setSelectedPost(post);
  };

  const closePostModal = () => {
    setSelectedPost(null);
  };

  if (loading) {
    return (
      <div className="space-y-12">
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('blog.title')}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            {t('blog.subtitle')}
          </p>
        </motion.header>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
          {t('blog.title')}
        </h1>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto">
          {t('blog.subtitle')}
        </p>
      </motion.header>

      {/* Featured Posts */}
      <motion.section
        ref={ref}
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="space-y-6"
      >
        <h2 className="text-2xl font-bold text-white mb-6">
          {t('blog.featured')}
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {featuredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              whileHover={{ scale: 1.02 }}
              className="group cursor-pointer"
              onClick={() => openPostModal(post)}
            >
              <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden transition-all duration-300 hover:border-gray-600 hover:shadow-xl">
                <div className="relative overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {t('blog.featured')}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-gray-400 text-sm mb-3">
                    <div className="flex items-center space-x-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{post.publishDate}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <ClockIcon className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-primary-300 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-primary-400 text-sm font-medium">
                      {categories.find(c => c.id === post.category)?.name}
                    </span>
                    <ArrowRightIcon className="w-5 h-5 text-primary-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </motion.section>

      {/* Search and Filter */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="space-y-6"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <h2 className="text-2xl font-bold text-white">
            {t('blog.allPosts')}
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('blog.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-primary-500 focus:outline-none w-full sm:w-64"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="bg-gray-800 border border-gray-700 rounded-lg text-white px-3 py-2 focus:border-primary-500 focus:outline-none"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Blog Posts Grid */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`${selectedCategory}-${searchTerm}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="group cursor-pointer"
                onClick={() => openPostModal(post)}
              >
                <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden transition-all duration-300 hover:border-gray-600">
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-center space-x-3 text-gray-400 text-xs mb-2">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="w-3 h-3" />
                        <span>{post.publishDate}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="w-3 h-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                      {post.tags.length > 2 && (
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                          +{post.tags.length - 2}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-primary-400 text-xs font-medium">
                        {categories.find(c => c.id === post.category)?.name}
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-primary-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </AnimatePresence>
        
        {filteredPosts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-400 mb-4">
              <MagnifyingGlassIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">{t('blog.noResults')}</p>
              <p className="text-sm">{t('blog.tryDifferentSearch')}</p>
            </div>
          </motion.div>
        )}
      </motion.section>

      {/* Blog Post Modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closePostModal}
          >
            <motion.article
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-64 lg:h-80 object-cover"
                />
                <button
                  onClick={closePostModal}
                  className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-8">
                <div className="flex items-center space-x-4 text-gray-400 text-sm mb-4">
                  <div className="flex items-center space-x-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{selectedPost.publishDate}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <ClockIcon className="w-4 h-4" />
                    <span>{selectedPost.readTime}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <TagIcon className="w-4 h-4" />
                    <span>{categories.find(c => c.id === selectedPost.category)?.name}</span>
                  </div>
                </div>
                
                <h1 className="text-3xl font-bold text-white mb-6">
                  {selectedPost.title}
                </h1>
                
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                    {selectedPost.content}
                  </p>
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('blog.tags')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedPost.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Blog;